import React, { useRef } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { v4 as uuid } from "uuid";
import { Row, Col, Input, Select, DatePicker, Radio } from "antd";
import { DrawerContainer, Footer } from "../styles";
import ButtonStandard from "../../../../components/utils/button";
import RadioButton from "../../../../components/utils/radioButton";
import NumberFormat from "react-number-format";

const { Option } = Select;
const dateFormat = "DD/MM/YYYY";

const GENDER_OPTIONS = [
  { value: "V", labelKey: "vaca" },
  { value: "N", labelKey: "novilha" },
  { value: "I", labelKey: "machoInteiro" },
  { value: "C", labelKey: "machoCastrado" },
];

const HerdFormDrawer = ({
  visible,
  data,
  breeds,
  supplements,
  cattleProducerProperties,
  multipleFarm,
  onSave,
  onCancel,
  translation,
}) => {
  const formikRef = useRef(null);

  const herdValidationSchema = Yup.object({
    amountAnimals: Yup.number().min(1).required(),
    gender: Yup.string().required(),
    breedId: Yup.string().required(),
    weightAverage: Yup.number().min(0).required(),
    useSisbov: Yup.boolean().required(),
    trace: Yup.boolean().required(),
    entryDate: Yup.date().required(),
    cattleProducerFarmId: multipleFarm
      ? Yup.string().required()
      : Yup.string().nullable(),
  });

  const initialHerdValues = {
    id: data?.id || uuid(),
    amountAnimals: data?.amountAnimals || null,
    breedId: data?.breedId || null,
    gender: data?.gender || null,
    weightAverage: data?.weightAverage || null,
    useSisbov: data?.useSisbov || false,
    trace: data?.trace || false,
    entryDate: data?.entryDate ? moment(data.entryDate) : null,
    cattleProducerFarmId: data?.cattleProducerFarmId || null,
    supplementId: data?.supplementId || null,
    averageOffer: data?.averageOffer ?? null,
    numberOfDays: data?.numberOfDays ?? null,
    costPerKilogramDryMatter: data?.costPerKilogramDryMatter ?? null,
    carcassHarnessingPercentage: data?.carcassHarnessingPercentage ?? null,
  };
  return (
    <DrawerContainer
      title={translation.boitelContractsPage.form.herdForm.title}
      width={600}
      visible={visible}
      onClose={onCancel}
      maskClosable={false}
    >
      <Formik
        ref={formikRef}
        initialValues={initialHerdValues}
        validationSchema={herdValidationSchema}
        onSubmit={(values) => {
          onSave(values);
        }}
        render={({
          values,
          errors,
          submitCount,
          setFieldValue,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className="drawerForm">
              <Row type="flex" gutter={16}>
                <Col span={12}>
                  <Row>
                    <label
                      className={
                        errors.amountAnimals && submitCount > 0 ? "error" : ""
                      }
                    >
                      {translation.boitelContractsPage.form.herds.amountAnimals}
                      *
                    </label>
                  </Row>
                  <Row>
                    <Input
                      type="number"
                      value={values.amountAnimals || ""}
                      placeholder={
                        translation.boitelContractsPage.form.herds
                          .amountAnimalsPlaceholder
                      }
                      onChange={(e) =>
                        setFieldValue(
                          "amountAnimals",
                          parseInt(e.target.value) || null,
                        )
                      }
                    />
                  </Row>
                </Col>
                <Col span={12}>
                  <Row>
                    <label
                      className={
                        errors.breedId && submitCount > 0 ? "error" : ""
                      }
                    >
                      {translation.boitelContractsPage.form.herds.breed}*
                    </label>
                  </Row>
                  <Row>
                    <Select
                      value={values.breedId || undefined}
                      placeholder={
                        translation.boitelContractsPage.form.herds
                          .breedPlaceholder
                      }
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.props.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      onChange={(value) => setFieldValue("breedId", value)}
                    >
                      {breeds.map((breed) => (
                        <Option key={breed.id} value={breed.id}>
                          {breed.name}
                        </Option>
                      ))}
                    </Select>
                  </Row>
                </Col>
              </Row>

              <Row type="flex" gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Row>
                    <label
                      className={
                        errors.gender && submitCount > 0 ? "error" : ""
                      }
                    >
                      {translation.boitelContractsPage.form.herds.gender}*
                    </label>
                  </Row>
                  <Row>
                    <Select
                      value={values.gender || undefined}
                      placeholder={
                        translation.boitelContractsPage.form.herds
                          .genderPlaceholder
                      }
                      allowClear
                      onChange={(value) => setFieldValue("gender", value)}
                    >
                      {GENDER_OPTIONS.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {
                            translation.boitelContractsPage.form.herds
                              .genderOptions[opt.labelKey]
                          }
                        </Option>
                      ))}
                    </Select>
                  </Row>
                </Col>
              </Row>

              <Row type="flex" gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Row>
                    <label
                      className={
                        errors.weightAverage && submitCount > 0 ? "error" : ""
                      }
                    >
                      {translation.boitelContractsPage.form.herds.weightAverage}
                      *
                    </label>
                  </Row>
                  <Row>
                    <NumberFormat
                      customInput={Input}
                      value={values.weightAverage || ""}
                      placeholder={translation.defaultPlaceholder}
                      decimalScale={2}
                      decimalSeparator=","
                      thousandSeparator="."
                      fixedDecimalScale={true}
                      addonAfter="Kg"
                      onValueChange={({ floatValue }) =>
                        setFieldValue("weightAverage", floatValue)
                      }
                    />
                  </Row>
                </Col>
                <Col span={12}>
                  <Row>
                    <label
                      className={
                        errors.entryDate && submitCount > 0 ? "error" : ""
                      }
                    >
                      {translation.boitelContractsPage.form.herds.entryDate}*
                    </label>
                  </Row>
                  <Row>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={values.entryDate ? moment(values.entryDate) : null}
                      format={dateFormat}
                      placeholder={translation.defaultDatePickerPlaceholder}
                      onChange={(date) => setFieldValue("entryDate", date)}
                    />
                  </Row>
                </Col>
              </Row>

              <Row type="flex" gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Row>
                    <label>
                      {translation.boitelContractsPage.form.herds.useSisbov}*
                    </label>
                  </Row>
                  <Row>
                    <Radio.Group
                      value={values.useSisbov}
                      onChange={(e) => {
                        if (e.target.value) {
                          setFieldValue("trace", true);
                        }
                        setFieldValue("useSisbov", e.target.value);
                      }}
                    >
                      <Row type="flex" gutter={8}>
                        <Col>
                          <RadioButton value={true}>Sim</RadioButton>
                        </Col>
                        <Col>
                          <RadioButton value={false}>Não</RadioButton>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Row>
                </Col>
                <Col span={12}>
                  <Row>
                    <label>
                      {translation.boitelContractsPage.form.herds.trace}*
                    </label>
                  </Row>
                  <Row>
                    <Radio.Group
                      value={values.trace}
                      onChange={(e) => {
                        setFieldValue("trace", e.target.value);
                      }}
                    >
                      <Row type="flex" gutter={8}>
                        <Col>
                          <RadioButton value={true}>Sim</RadioButton>
                        </Col>
                        <Col>
                          <RadioButton value={false}>Não</RadioButton>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Row>
                </Col>
              </Row>

              {multipleFarm && (
                <Row type="flex" gutter={16} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <Row>
                      <label
                        className={
                          errors.cattleProducerFarmId && submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.boitelContractsPage.form.herds.farm}*
                      </label>
                    </Row>
                    <Row>
                      <Select
                        value={values.cattleProducerFarmId || undefined}
                        placeholder={
                          translation.boitelContractsPage.form.herds
                            .farmPlaceholder
                        }
                        onChange={(value) =>
                          setFieldValue("cattleProducerFarmId", value)
                        }
                      >
                        {cattleProducerProperties.map((property) => (
                          <Option key={property.id} value={property.id}>
                            {property.name}
                          </Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                </Row>
              )}

              <Row type="flex" gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Row>
                    <label>
                      {translation.boitelContractsPage.form.herds.supplement}
                    </label>
                  </Row>
                  <Row>
                    <Select
                      value={values.supplementId || undefined}
                      placeholder={
                        translation.boitelContractsPage.form.herds
                          .supplementPlaceholder
                      }
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.props.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      onChange={(value) => setFieldValue("supplementId", value)}
                    >
                      {supplements.map((supplement) => (
                        <Option key={supplement.id} value={supplement.id}>
                          {supplement.name}
                        </Option>
                      ))}
                    </Select>
                  </Row>
                </Col>
              </Row>
            </div>
            <Footer>
              <Row type="flex">
                <Col span={24} className="buttonsDiv">
                  <ButtonStandard
                    type="button"
                    buttonType="type7"
                    onClick={onCancel}
                  >
                    {translation.buttons.cancel}
                  </ButtonStandard>
                  <ButtonStandard type="submit" buttonType="type6">
                    {translation.buttons.save}
                  </ButtonStandard>
                </Col>
              </Row>
            </Footer>
          </form>
        )}
      />
    </DrawerContainer>
  );
};

export default HerdFormDrawer;
